import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useCart } from '../contexts/CartContext';
import logo from '../logo.png';

const HeaderWrapper = styled.header`
  background-color: var(--color-background);
  border-bottom: 1px solid var(--color-border);
  position: sticky;
  top: 0;
  z-index: 100;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-4) 0;
  position: relative;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  
  img {
    height: 120px;
    width: auto;
    transition: transform 0.2s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    img {
      height: 90px;
    }
  }
`;

const NavLinks = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-8);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--spacing-4);
    display: ${props => props.isOpen ? 'flex' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: var(--color-background);
    border-bottom: 1px solid var(--color-border);
    padding: var(--spacing-4) 0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  
  @media (min-width: 769px) {
    display: flex !important;
    position: static;
    background: none;
    border: none;
    padding: 0;
    box-shadow: none;
  }
`;

const NavLink = styled(Link)`
  color: var(--color-text);
  text-decoration: none;
  font-weight: 400;
  transition: color 0.2s ease;
  
  &:hover {
    color: var(--color-secondary);
  }
`;

const CartIcon = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  color: var(--color-text);
  text-decoration: none;
  
  &:hover {
    color: var(--color-secondary);
  }
`;

const CartCount = styled.span`
  background-color: var(--color-secondary);
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-xs);
  position: absolute;
  top: -8px;
  right: -8px;
`;

const MobileMenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  flex-direction: column;
  gap: 3px;
  
  span {
    width: 25px;
    height: 3px;
    background-color: var(--color-secondary);
    transition: all 0.2s ease;
  }
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const Header: React.FC = () => {
  const { totalItems } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  // Close menu when clicking outside or on escape key
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (mobileMenuOpen && !target.closest('nav')) {
        setMobileMenuOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [mobileMenuOpen]);

  return (
    <HeaderWrapper>
      <div className="container">
        <Nav>
          <Logo to="/" onClick={handleNavClick}>
            <img src={logo} alt="Lillaflora - Mysig blomsterbutik i Kramfors" />
          </Logo>
          
          <MobileMenuToggle onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </MobileMenuToggle>
          
          <NavLinks isOpen={mobileMenuOpen}>
            <NavLink to="/" onClick={handleNavClick}>Hem</NavLink>
            <NavLink to="/produkter" onClick={handleNavClick}>Produkter</NavLink>
            <NavLink to="/begravningar" onClick={handleNavClick}>Begravningar</NavLink>
            <NavLink to="/blombud" onClick={handleNavClick}>Blombud</NavLink>
            <NavLink to="/floristbloggen" onClick={handleNavClick}>Floristbloggen</NavLink>
            <NavLink to="/kontakt" onClick={handleNavClick}>Kontakta oss</NavLink>
            <CartIcon to="/kundvagn" onClick={handleNavClick}>
              🛒 Kundvagn
              {totalItems > 0 && <CartCount>{totalItems}</CartCount>}
            </CartIcon>
          </NavLinks>
        </Nav>
      </div>
    </HeaderWrapper>
  );
};

export default Header;
